import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Plus, Search, Pencil, Trash2, Power, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { useSingleFlightMutation } from "@/hooks/useSingleFlightMutation";

const EMPTY = {
  name: "",
  address: "",
  notes: "",
  whatsapp: "",
  sort_order: 0,
  active: true,
};

export default function ZonesPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const { data: zones = [], isLoading } = useQuery({
    queryKey: ["zones"],
    queryFn: async () => (await api.get("/zones")).data,
  });

  const filtered = useMemo(
    () => zones.filter((z) =>
      !search
      || (z.name || "").toLowerCase().includes(search.toLowerCase())
      || (z.address || "").toLowerCase().includes(search.toLowerCase())
    ),
    [zones, search]
  );

  const openCreate = () => { setEditing(null); setForm(EMPTY); setModalOpen(true); };
  const openEdit = (z) => { setEditing(z); setForm({ ...EMPTY, ...z }); setModalOpen(true); };

  const saveMut = useSingleFlightMutation({
    mutationFn: async () => {
      const payload = {
        ...form,
        name: (form.name || "").trim(),
        address: (form.address || "").trim(),
        notes: (form.notes || "").trim(),
        whatsapp: (form.whatsapp || "").trim(),
        sort_order: Number(form.sort_order) || 0,
      };
      if (editing) return (await api.put(`/zones/${editing.id}`, payload)).data;
      return (await api.post("/zones", payload)).data;
    },
    onSuccess: () => {
      toast.success(editing ? "Zon modifye" : "Zon ajoute");
      qc.invalidateQueries({ queryKey: ["zones"] });
      setModalOpen(false);
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/zones/${id}`)).data,
    onSuccess: () => { toast.success("Zon efase"); qc.invalidateQueries({ queryKey: ["zones"] }); },
  });

  const toggleActive = useMutation({
    mutationFn: async (z) => (await api.put(`/zones/${z.id}`, { active: !z.active })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["zones"] }),
  });

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
            <MapPin className="h-8 w-8 text-brand-blue" /> Zon / Adrès
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            Lis zon kliyan yo chwazi sou WhatsApp pou jwenn <strong>adrès presi</strong>.
            Bot la <strong>pa bay enfòmasyon Zelle</strong> jiskaske kliyan an chwazi yon zon —
            paske se pa sèvis transfè an liy: yo dwe <strong>pase pran</strong> nan adrès la.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="add-zone-btn" className="bg-brand-blue hover:bg-brand-blueHover text-white gap-2">
          <Plus className="h-4 w-4" /> Ajoute zon
        </Button>
      </motion.div>

      <div className="rounded-2xl border border-border bg-card">
        <div className="p-4 border-b border-border flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Chèche zon oswa adrès"
              className="pl-9 h-9"
              data-testid="zone-search-input"
            />
          </div>
          <div className="text-xs text-muted-foreground">{filtered.length} zon</div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>Zon</TableHead>
                <TableHead>Adrès presi</TableHead>
                <TableHead>Nòt</TableHead>
                <TableHead>WhatsApp</TableHead>
                <TableHead>Estati</TableHead>
                <TableHead className="text-right">Aksyon</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && [...Array(2)].map((_, i) => (
                <TableRow key={i}><TableCell colSpan={7}><Skeleton className="h-8" /></TableCell></TableRow>
              ))}
              {!isLoading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-10">
                    Pa gen zon — ajoute premye a pou bot kliyan an ka bay adrès.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((z) => (
                <TableRow key={z.id} data-testid={`zone-row-${z.id}`}>
                  <TableCell className="text-muted-foreground">{z.sort_order ?? 0}</TableCell>
                  <TableCell className="font-medium">{z.name}</TableCell>
                  <TableCell className="text-sm max-w-xs truncate">{z.address}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[10rem] truncate">{z.notes || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{z.whatsapp || "—"}</TableCell>
                  <TableCell>
                    <Badge variant={z.active !== false ? "default" : "outline"} className={z.active !== false ? "bg-emerald-500/20 text-emerald-500 border-emerald-500/30" : ""}>
                      {z.active !== false ? "Aktif" : "Inaktif"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button variant="ghost" size="icon" onClick={() => toggleActive.mutate(z)} data-testid={`toggle-zone-${z.id}`}>
                      <Power className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(z)} data-testid={`edit-zone-${z.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => window.confirm("Efase zon sa a?") && deleteMut.mutate(z.id)} data-testid={`delete-zone-${z.id}`}>
                      <Trash2 className="h-4 w-4 text-rose-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-card max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifye zon" : "Ajoute zon"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div>
              <Label>Non zon</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="egz. Delmas, Cap-Haïtien…"
                data-testid="zone-form-name"
              />
            </div>
            <div>
              <Label>Adrès presi</Label>
              <Textarea
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Adrès konplè kliyan an ap wè"
                rows={3}
                data-testid="zone-form-address"
              />
            </div>
            <div>
              <Label>Nòt (orè, wout…)</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Opsyonèl"
                rows={2}
                data-testid="zone-form-notes"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>WhatsApp lokal</Label>
                <Input
                  value={form.whatsapp}
                  onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                  placeholder="Opsyonèl"
                  data-testid="zone-form-whatsapp"
                />
              </div>
              <div>
                <Label>Ordre (lis)</Label>
                <Input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                  data-testid="zone-form-order"
                />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2">
              <div className="text-sm font-medium">Aktif (montre sou WhatsApp)</div>
              <Switch checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="zone-form-active" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Anile</Button>
            <Button
              className="bg-brand-blue hover:bg-brand-blueHover text-white"
              disabled={!form.name.trim() || !form.address.trim() || saveMut.isPending}
              onClick={() => saveMut.mutate()}
              data-testid="zone-form-save"
            >
              Sove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
